import { typography } from '@/shared/theme/tokens';
import { Sparkline } from './Sparkline';
import { LIVE_STATUS_META, CATEGORY_LABEL, TERM } from '../liveModeMeta';

const MONO = typography.fontFamily.mono;

/**
 * One alert row in the terminal log. Colour tracks the value's *current* standing
 * (a recovered metric reads OK/green). Leading status block + monospace value —
 * no side-stripe accent. Stale rows grey out and never flash.
 */
export function AlertTickerItem({ alert, flashing, animate, onSelect }) {
  const stale = !!alert.isStale;
  const meta = stale
    ? { color: TERM.inkFaint, border: TERM.line, bgDark: 'transparent', label: 'STALE' }
    : (LIVE_STATUS_META[alert.status] ?? LIVE_STATUS_META.ok);
  const isFlashing = flashing && !stale;
  const clickable = typeof onSelect === 'function';
  const critical = alert.status === 'critical' && !stale;

  // Critical rows carry a faint standing tint; a flash briefly brightens it.
  const baseBg = isFlashing ? meta.bgDark : critical ? meta.bgDark : 'transparent';

  return (
    <div
      onClick={clickable ? () => onSelect(alert) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(alert); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${alert.parameterName} alert` : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
        borderBottom: `1px solid ${TERM.line}`,
        background: baseBg, opacity: stale ? 0.6 : 1,
        cursor: clickable ? 'pointer' : 'default',
        transition: animate ? 'background 0.4s ease' : 'none',
      }}
    >
      {/* status block (contained marker, not a side-stripe) */}
      <span style={{
        width: 10, height: 10, flexShrink: 0,
        background: stale ? 'transparent' : meta.color,
        border: stale ? `1px solid ${TERM.lineBright}` : 'none',
        boxShadow: isFlashing ? `0 0 8px ${meta.color}` : 'none',
      }} />

      {/* code + name + category */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{
            fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
            letterSpacing: typography.letterSpacing.wide, color: meta.color, flexShrink: 0, width: 30,
          }}>
            {meta.label}
          </span>
          <span style={{
            fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.md,
            fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide,
            textTransform: 'uppercase', color: TERM.ink,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {alert.parameterName}
          </span>
        </div>
        <div style={{
          fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint,
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', marginTop: 2, paddingLeft: 38,
        }}>
          {CATEGORY_LABEL[alert.category] ?? alert.category}
        </div>
      </div>

      <div style={{ width: 56, flexShrink: 0, opacity: stale ? 0.4 : 0.9 }}>
        <Sparkline data={alert.buffer} color={meta.color} height={24} animate={false} />
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, flexShrink: 0, minWidth: 60, justifyContent: 'flex-end' }}>
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold,
          color: meta.color, fontVariantNumeric: 'tabular-nums',
        }}>
          {stale ? '——' : alert.liveValue}
        </span>
        {alert.unit && !stale && (
          <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint }}>{alert.unit}</span>
        )}
      </div>
    </div>
  );
}
